import { useNetworkTypeOptions, useUnitChoices } from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/ui/use-toast";
import {
  DEFAULT_PAGE_NUMBER,
  FIELD_CLASSES,
  PAGE_SIZE,
} from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAddNetwork,
  useBackupStatus,
  useDMADistributionNetworks,
  useDestinationNetwork,
  useDetails,
  useDistributionNetworks,
  useOperationalStatus,
  useOriginFacilities,
  useTerritoryAreas,
  useTerritoryZones,
  useTransmissionNetworks,
  useUpdateNetwork,
  useUserInfo,
} from "../../hooks";

interface NetworkDetailsStepProps {
  editMode?: boolean;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  onStepComplete?: (stepData: any) => void;
}

const NetworkDetailsStep: React.FC<NetworkDetailsStepProps> = ({
  onNext,
  onStepComplete,
  setValidationError,
  clearValidationError,
}) => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = getRemoteUtilityId();
  const addNetworkMutation = useAddNetwork();
  const updateNetworkMutation = useUpdateNetwork();
  const [createdNetworkId, setCreatedNetworkId] = useState<string | null>(null);
  const dataLoadedRef = useRef(false);

  const { data: networkTypeData } = useNetworkTypeOptions();
  const { data: detailData, refetch: refetchDetails } = useDetails({
    networkId: id || createdNetworkId || "",
  });
  const { data: backupStatusData } = useBackupStatus();
  const { data: operationalStatusData } = useOperationalStatus();
  const { data: userInfoData } = useUserInfo({
    remote_utility_id: remoteUtilityId,
    page: DEFAULT_PAGE_NUMBER,
    limit: PAGE_SIZE,
    role: "Plant Supervisor",
  });
  const { data: originFacilitiesData } = useOriginFacilities({
    is_all_data: 1,
    remote_utility_id: remoteUtilityId,
  });

  const { data: zoneData } = useTerritoryZones({
    remote_utility_id: remoteUtilityId,
    show_inactive: false,
  });
  const { data: areaData } = useTerritoryAreas({
    remote_utility_id: remoteUtilityId,
    show_inactive: false,
  });
  const { data: transmissionNetworkData } = useTransmissionNetworks({
    remote_utility_id: remoteUtilityId,
    is_pagination_required: 0,
  });
  const { data: DMADistributionNetworksData } = useDMADistributionNetworks({
    remote_utility_id: remoteUtilityId,
    is_pagination_required: 0,
  });
  const { data: distributionNetworksData } = useDistributionNetworks({
    remote_utility_id: remoteUtilityId,
    is_pagination_required: 0,
  });
  const { data: destinationNetworkData } = useDestinationNetwork({
    type: "distribution",
    remote_utility_id: remoteUtilityId,
    is_pagination_required: 0,
  });
  const { data: unitList } = useUnitChoices();
  const capacityUnitOptions = useMemo(() => {
    return (
      unitList?.map((unit: any) => ({
        value: unit.key,
        label: unit.value,
      })) || []
    );
  }, [unitList]);

  useEffect(() => {
    if (id || createdNetworkId) {
      refetchDetails();
      dataLoadedRef.current = false;
    }
  }, [id, createdNetworkId, refetchDetails]);

  const operatorsMap = useMemo(() => {
    if (!userInfoData?.result) return {};

    return userInfoData.result.reduce((acc: any, user: any) => {
      acc[user.user.id] = {
        name: user.user.name,
        email: user.user.email,
      };
      return acc;
    }, {});
  }, [userInfoData]);

  const formOptions = useMemo(
    () => ({
      networkTypeOptions:
        networkTypeData?.map((item: any) => ({
          value: item.code,
          label: item.name,
        })) || [],

      destinationTypeOptions:
        destinationNetworkData?.result?.map((item: any) => ({
          value: item.id,
          label: item.networkName,
        })) || [],

      parentTransmissionNetworkOptions:
        transmissionNetworkData?.result?.map((item: any) => ({
          value: item.id,
          label: item.networkName,
        })) || [],

      dmasConnectedOptions:
        DMADistributionNetworksData?.result?.map((item: any) => ({
          value: item.id,
          label: item.networkName,
        })) || [],

      parentDistributionNetworkOptions:
        distributionNetworksData?.result?.map((item: any) => ({
          value: item.id,
          label: item.networkName,
        })) || [],

      originFacilitiesOptions:
        originFacilitiesData?.results?.map((item: any) => ({
          value: item.id,
          label: item.facilityName,
        })) || [],

      unitOptions:
        unitList?.map((unit: any) => ({
          value: unit.key,
          label: unit.value,
        })) || [],

      backupStatusOptions:
        backupStatusData?.result?.map((item: any) => ({
          value: item.key,
          label: item.value,
        })) || [],

      operationalStatusOptions:
        operationalStatusData?.map((item: any) => ({
          value: item.key,
          label: item.value,
        })) || [],

      networkOperatorOptions:
        userInfoData?.result?.map((user: any) => ({
          value: user.user.id,
          label: user.user.name,
        })) || [],

      zoneOptions:
        zoneData?.result?.map((zone: any) => ({
          value: zone.hierarchyCodes.zone,
          label: zone.hierarchyNames.zone,
        })) || [],

      areaOptions:
        areaData?.result?.map((area: any) => ({
          value: area.hierarchyCodes.area,
          label: area.hierarchyNames.area,
        })) || [],
    }),
    [
      networkTypeData,
      destinationNetworkData,
      transmissionNetworkData,
      DMADistributionNetworksData,
      distributionNetworksData,
      originFacilitiesData,
      unitList,
      backupStatusData,
      operationalStatusData,
      userInfoData,
      zoneData,
      areaData,
    ]
  );

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "networkName",
        label: "Network Name",
        type: "text",
        required: true,
        placeholder: "e.g., CBD Distribution Loop",
        defaultValue: "",
        maxLength: 120,
        tooltipText:
          "Enter a unique, descriptive name for this network (e.g., 'CBD Distribution Loop').",
        showHelperTooltip: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "networkType",
        label: "Network Type",
        type: "select",
        required: true,
        defaultValue: "",
        placeholder: "Select network type...",
        tooltipText:
          "Select the classification that best describes this network's function. This will determine the required operational details.",
        showHelperTooltip: true,
        options: formOptions.networkTypeOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "operationalStatus",
        label: "Operational Status",
        type: "select",
        required: true,
        defaultValue: "",

        placeholder: "Select status...",
        tooltipText: "The current operational status of the network.",
        showHelperTooltip: true,
        options: formOptions.operationalStatusOptions,
        className: "col-span-1 mt-3",
      },

      {
        name: "networkOperator",
        label: "Network Operator",
        type: "select",
        required: true,
        placeholder: "Select operator...",
        tooltipText:
          "Select the primary team or individual responsible for operating this network.",
        showHelperTooltip: true,
        options: formOptions.networkOperatorOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "contact",
        label: "Contact",
        type: "email",
        required: false,
        tooltipText:
          "The primary contact person for the selected network operator. This is populated automatically.",
        showHelperTooltip: true,
        placeholder: "Auto-populated based on operator",
        disabled: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "backupStatus",
        label: "Backup Status",
        type: "select",
        required: true,
        placeholder: "Select backup status...",
        tooltipText:
          "The level of redundancy or backup available for this network in case of failure.",
        showHelperTooltip: true,
        options: formOptions.backupStatusOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "designCapacity",
        label: "Design Capacity",
        type: "decimal",
        required: true,
        placeholder: "Enter capacity...",
        tooltipText:
          "The maximum designed flow or capacity of this network in Megaliters Per Day (MLD).",
        showHelperTooltip: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "unit",
        label: "Unit",
        type: "select",
        placeholder: "Select unit...",
        options: formOptions.unitOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "installationDate",
        label: "Installation Date",
        type: "datepicker",
        required: true,
        defaultValue: "",
        tooltipText: "The date when the network was installed.",
        showHelperTooltip: true,
        className: "col-span-1 mt-3",
      },
      {
        name: "description",
        label: "Description / Notes",
        type: "textarea",
        placeholder: "Optional description or notes about this network",
        tooltipText:
          "Optional. Add any relevant notes, history, or context about this network.",
        showHelperTooltip: true,
        rows: 4,
        className: "col-span-2 mt-3",
      },

      // TRANSMISSION NETWORK FIELDS
      {
        name: "originFacility",
        label: "Origin Facility",
        type: "select",
        required: false,
        group: "Transmission Network Details",
        groupColorNeeded: false,
        placeholder: "Select origin facility...",
        showWhen: (formValues) => formValues.networkType === "transmission#1",
        options: formOptions.originFacilitiesOptions,
        className: "col-span-1 mt-3",
      },
      // {
      //   name: "destinationNetworks",
      //   label: "Destination Networks",
      //   type: "multi-select",
      //   group: "Transmission Network Details",
      //   placeholder: "Select destination networks...",
      //   showWhen: (formValues) => formValues.networkType === "transmission",
      //   options: formOptions.destinationTypeOptions,
      //   className: "col-span-1 mt-3",
      // },
      {
        name: "pressureRange",
        label: "Pressure Range (bar)",
        type: "custom",
        group: "Transmission Network Details",
        showWhen: (formValues) => formValues.networkType === "transmission#1",
        className: "col-span-1 mt-3",
        customRender: (field, form, error) => (
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Controller
                  name="pressureRangeMin"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
              <span className="text-gray-500 font-medium">to</span>
              <div className="flex-1">
                <Controller
                  name="pressureRangeMax"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        name: "customerConnectionsServed",
        label: "Customer Connections Served",
        type: "number",
        min: 0,
        required: false,
        group: "Transmission Network Details",
        placeholder: "Enter number of connections...",
        showWhen: (formValues) => formValues.networkType === "transmission#1",
        className: "col-span-1 mt-3",
      },

      // DISTRIBUTION NETWORK FIELDS
      {
        name: "zones",
        label: "Zones",
        type: "multi-select",
        group: "Distribution Network Details",
        groupColorNeeded: false,
        placeholder: "Select zones...",
        showWhen: (formValues) => formValues.networkType === "distribution#2",
        options: formOptions.zoneOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "parentTransmission",
        label: "Parent Transmission Network",
        type: "select",
        group: "Distribution Network Details",
        placeholder: "Select parent transmission...",
        showWhen: (formValues) => formValues.networkType === "distribution#2",
        options: formOptions.parentTransmissionNetworkOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "pressureRange",
        label: "Pressure Range (bar)",
        type: "custom",
        group: "Distribution Network Details",
        showWhen: (formValues) => formValues.networkType === "distribution#2",
        className: "col-span-1 mt-3",
        customRender: (field, form, error) => (
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Controller
                  name="pressureRangeMin"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
              <span className="text-gray-500 font-medium">to</span>
              <div className="flex-1">
                <Controller
                  name="pressureRangeMax"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ),
      },

      // {
      //   name: "dmasConnected",
      //   label: "DMAs Connected",
      //   type: "multi-select",
      //   group: "Distribution Network Details",
      //   placeholder: "Select DMAs...",
      //   showWhen: (formValues) => formValues.networkType === "distribution",
      //   options: formOptions.dmasConnectedOptions,
      //   className: "col-span-1 mt-3",
      // },

      // DMA FIELDS
      {
        name: "parentDistribution",
        label: "Parent Distribution Network",
        type: "select",
        required: false,
        group: "DMA Details",
        groupColorNeeded: false,
        placeholder: "Select parent distribution...",
        showWhen: (formValues) => formValues.networkType === "dma#3",
        options: formOptions.parentDistributionNetworkOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "areas",
        label: "Areas",
        type: "multi-select",
        required: false,
        group: "DMA Details",
        placeholder: "Select areas...",
        showWhen: (formValues) => formValues.networkType === "dma#3",
        options: formOptions.areaOptions,
        className: "col-span-1 mt-3",
      },
      {
        name: "pressureRange",
        label: "Pressure Range (bar)",
        type: "custom",
        group: "DMA Details",
        showWhen: (formValues) => formValues.networkType === "dma#3",
        className: "col-span-1 mt-3",
        customRender: (field, form, error) => (
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Controller
                  name="pressureRangeMin"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Min"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
              <span className="text-gray-500 font-medium">to</span>
              <div className="flex-1">
                <Controller
                  name="pressureRangeMax"
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <input
                      {...controllerField}
                      type="number"
                      step="0.01"
                      placeholder="Max"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                />
              </div>
            </div>
            {error && <span className="text-red-500 text-sm">{error}</span>}
          </div>
        ),
      },
      {
        name: "customerConnectionsServed",
        label: "Customer Connections Served",
        type: "number",
        min: 0.0,
        default: 0.0,
        group: "DMA Details",
        placeholder: "Enter number of connections...",
        showWhen: (formValues) => formValues.networkType === "dma#3",
        className: "col-span-1 mt-3",
      },
    ],
    [formOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  useEffect(() => {
    if (
      (action === "edit" || createdNetworkId) &&
      detailData?.result &&
      !dataLoadedRef.current
    ) {
      const editData = {
        networkName: detailData.result?.networkName || "",
        networkType: detailData.result?.networkType || "",
        operationalStatus: detailData.result?.operationalStatus || 0,
        networkOperator: detailData.result?.networkOperatorId || "",
        contact: detailData.result?.contact || "",
        unit: detailData.result?.additionalData?.unit || "",
        installationDate: detailData.result?.installationDate || "",
        backupStatus: detailData.result?.backupStatus || 0,
        designCapacity: detailData.result?.designCapacity || "",
        capacityUnit: detailData.result?.capacityUnit || "",
        description: detailData.result?.description || "",
        originFacility: detailData.result?.facility || "",
        destinationNetworks: detailData.result?.destinationNetworks || [],
        pressureRangeMin:
          detailData.result?.networkConfig?.pressureRange?.min || "",
        pressureRangeMax:
          detailData.result?.networkConfig?.pressureRange?.max || "",
        customerConnectionsServed: detailData.result?.customerConnections || "",
        zones: detailData.result?.zoneCodes || [],
        parentTransmission: detailData.result?.parentNetwork || "",
        dmasConnected: detailData.result?.dmasConnected || [],
        parentDistribution: detailData.result?.parentDistribution || "",
        areas: detailData.result?.areaCodes || [],
      };

      FormService.populateForm(form, editData, formFields);
      dataLoadedRef.current = true;
    }
  }, [action, detailData, form, createdNetworkId]);

  const formValues = form.watch();

  useEffect(() => {
    if (
      formValues.networkOperator &&
      operatorsMap[formValues.networkOperator]
    ) {
      const operator = operatorsMap[formValues.networkOperator];
      if (formValues.contact !== operator.email) {
        form.setValue("contact", operator.email);
      }
    }
  }, [formValues.networkOperator, operatorsMap, form]);

  const validateStep = () => {
    const requiredFields = [
      "networkName",
      "networkType",
      "networkOperator",
      "contact",
      "designCapacity",
    ];

    const missingFields = requiredFields.filter((field) => !formValues[field]);

    if (missingFields.length > 0) {
      setValidationError?.(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return false;
    }

    clearValidationError?.();
    return true;
  };

  const handleNext = async () => {
    const isValid = await FormService.validateForm(form);

    if (!isValid) {
      if (setValidationError) {
        setValidationError("Please fill in all required fields.");
      }
      toast({
        title: "Please fill in all required fields.",
        variant: "destructive",
      });

      return;
    }
    try {
      const selectedContact = formOptions.networkOperatorOptions.find(
        (opt) => opt.value === Number(formValues.networkOperator)
      );
      const payload = {
        network_name: formValues.networkName,
        network_type: formValues.networkType,
        operational_status: formValues.operationalStatus,
        backup_status: formValues.backupStatus,
        design_capacity: formValues.designCapacity,
        description: formValues.description,
        facility: formValues.originFacility,
        network_operator_id: formValues.networkOperator,
        contact_person: selectedContact.label,
        contact_name: formValues.contact,
        contact_phone: formValues.contactPhone,
        remote_utility_id: remoteUtilityId,
        installation_date: formValues.installationDate,
        zones:
          formValues.zones?.map((zoneCode) => {
            const zoneOption = formOptions.zoneOptions.find(
              (option) => option.value === zoneCode
            );
            return {
              code: zoneCode,
              name: zoneOption ? zoneOption.label : zoneCode,
            };
          }) || [],
        additional_data: {
          unit: formValues.unit,
        },
        areas:
          formValues.areas?.map((areaCode) => {
            const areaOption = formOptions.areaOptions.find(
              (option) => option.value === areaCode
            );
            return {
              code: areaCode,
              name: areaOption ? areaOption.label : areaCode,
            };
          }) || [],
        pressure_range_min: formValues.pressureRangeMin,
        pressure_range_max: formValues.pressureRangeMax,
        customer_connections: formValues.customerConnectionsServed || 0,
        destination_networks: formValues.destinationNetworks,
        parent_network: formValues.parentTransmission,
        parent_distribution: formValues.parentDistribution,
      };
      if (formValues.unit) {
        payload["unit"] = formValues.unit;
      }

      let networkId = id || createdNetworkId;

      if ((action === "edit" && id) || createdNetworkId) {
        await updateNetworkMutation.mutateAsync({
          networkId: networkId,
          payload,
        });
      } else {
        const response = await addNetworkMutation.mutateAsync(payload);
        networkId = response?.result?.id || response?.id;
        if (networkId) {
          setCreatedNetworkId(networkId);
          navigate(`/ax/asset-registry/networks/create/edit/${networkId}`, {
            replace: true,
          });
        }
      }

      if (onNext) {
        onNext();
      }

      if (onStepComplete) {
        onStepComplete({
          networkId: networkId,
          networkName: formValues.networkName,
        });
      }

      clearValidationError?.();
    } catch (error) {
      console.log("---catch", error);

      setValidationError?.(
        action === "edit" || createdNetworkId
          ? "Failed to update network. Please try again."
          : "Failed to create network. Please try again."
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <p className="page-title">Network Identification</p>
        <p className="page-subtitle mt-1">
          Enter the basic details for the network. Fields marked with * are
          required.
        </p>
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        gridColumns={2}
        onSubmit={handleNext}
        responsiveColumns={{
          sm: 1,
          md: 2,
        }}
        config={{
          showResetButton: false,
          showSubmitButton: false,
          classes: FIELD_CLASSES,
        }}
      />
      <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
        <Button
          onClick={handleNext}
          isLoading={
            updateNetworkMutation.isPending || addNetworkMutation.isPending
          }
        >
          Continue to Next Step
        </Button>
      </div>
    </div>
  );
};

export default NetworkDetailsStep;
